import { avatarDefault } from '@/assets/images';
import { Stack } from '@/components/layout';
import { drawerStateStore } from '@/components/ui/drawer/drawer-state-store';
import { Image } from '@/components/ui/image';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { STRINGS } from '@/constants/strings';
import { useDashboard } from '@/features/home/hooks/use-dashboard';
import { createStyles, useScaling } from '@/hooks';
import { googleSignOut } from '@/libs/firebase/auth';
import { resetRootToPostAuthTarget } from '@/navigations/resolve-post-auth-target';
import { useSessionStore } from '@/stores';
import { colors } from '@/theme/colors';
import { requestIdle } from '@/utils/idle-callback';
import type { DrawerContentComponentProps } from '@react-navigation/drawer';
import React, { useSyncExternalStore } from 'react';
import { Linking, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type AuthDrawerMenuItem = {
  routeName: string;
  label: string;
};

export type AuthDrawerContentProps = DrawerContentComponentProps & {
  menuItems?: AuthDrawerMenuItem[];
};

export function AuthDrawerContent(props: AuthDrawerContentProps) {
  const { navigation, menuItems = [] } = props;
  const insets = useSafeAreaInsets();
  const { userName, avatarSource } = useDashboard();
  const { scale } = useScaling();
  const styles = useStyles();
  const drawerDisplayState = useSyncExternalStore(
    drawerStateStore.subscribe,
    drawerStateStore.getSnapshot,
  );

  const avatarSize = scale({ phone: 54, tablet: 54 });
  const avatarRadius = avatarSize / 2;

  const handlePressItem = (routeName: string) => {
    if (routeName === 'Support') {
      Linking.openURL(`mailto:${STRINGS.drawer.supportEmail}`);
      navigation.closeDrawer();
    } else if (routeName === 'SwitchRole') {
      navigation.closeDrawer();
      // Clear the active role so the post-auth target resolves to RoleSelect.
      // Deferred like logout to avoid racing the drawer dismiss animation with a root reset.
      useSessionStore.getState().setActiveRole(null);
      requestIdle(() => resetRootToPostAuthTarget(), { timeout: 1000 });
    } else {
      navigation.navigate(routeName as never);
      navigation.closeDrawer();
    }
  };

  const handleLogout = () => {
    navigation.closeDrawer();
    // Soft logout: keep the Firebase session warm and route to the biometric/password login
    // screen for a quick re-entry (hard sign-out happens from there via the logout icon).
    // Deferred until the drawer animation finishes so the root `reset` from `withAuth` doesn't
    // race the native transition (observed to close/crash the app on some Android devices).
    googleSignOut();
    requestIdle(() => useSessionStore.getState().setSoftLoggedOut(true), { timeout: 1000 });
  };

  if (drawerDisplayState === 'hidden') {
    return null;
  }

  return (
    <Stack flex={1} style={styles.root}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        <Stack
          style={[styles.header, { paddingTop: insets.top + scale({ phone: 6, tablet: 23 }) }]}>
          <Stack direction="row" justify="flex-end" align="center" style={styles.closeRow}>
            <TouchableOpacity
              onPress={() => navigation.closeDrawer()}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.closeButton}>
              <Text
                // TODO(typography): replace manual font props with a `variant` token when available.
                fontFamily="Inter"
                fontWeight="400"
                fontSize={scale({ phone: 9, tablet: 12 })}
                fontStyle="normal"
                style={styles.closeLabel}>
                {STRINGS.drawer.close}
              </Text>
              <Stack marginLeft={scale(7)}>
                <SvgIcon name="IcClose" size={10} color={colors['10']} />
              </Stack>
            </TouchableOpacity>
          </Stack>
          <View style={styles.headerDivider} />
          <Stack direction="row" align="center" style={styles.profileRow}>
            <Stack
              width={avatarSize}
              height={avatarSize}
              style={[styles.avatarFrame, { borderRadius: avatarRadius }]}>
              <Image
                source={avatarSource ?? avatarDefault}
                width={'100%'}
                height={'100%'}
                resizeMode="cover"
                defaultSource={avatarDefault}
              />
            </Stack>
            <Stack marginLeft={scale(12)}>
              <Text
                // TODO(typography): replace manual font props with a `variant` token when available.
                fontFamily="Montserrat"
                fontWeight="800"
                fontSize={scale({ phone: 11, tablet: 16 })}
                transform="uppercase"
                style={styles.userName}>
                {userName}
              </Text>
              <Text
                // TODO(typography): replace manual font props with a `variant` token when available.
                fontFamily="Inter"
                fontWeight="400"
                fontSize={scale({ phone: 11, tablet: 12 })}
                fontStyle="normal"
                style={styles.userRole}>
                {STRINGS.drawer.role}
              </Text>
            </Stack>
          </Stack>
        </Stack>
        <View style={styles.headerDivider} />
        <View style={styles.menuContainer}>
          {menuItems.map(({ routeName, label }) => (
            <TouchableOpacity
              key={routeName}
              style={styles.menuItem}
              activeOpacity={0.7}
              onPress={() => handlePressItem(routeName)}>
              <Text
                // TODO(typography): replace manual font props with a `variant` token when available.
                fontFamily="Inter"
                fontWeight="400"
                fontSize={scale({ phone: 10, tablet: 14 })}
                style={styles.menuLabel}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}

          <TouchableOpacity style={styles.menuItem} activeOpacity={0.7} onPress={handleLogout}>
            <Text
              // TODO(typography): replace manual font props with a `variant` token when available.
              fontFamily="Inter"
              fontWeight="400"
              fontSize={scale({ phone: 10, tablet: 14 })}
              style={styles.menuLabel}>
              {STRINGS.drawer.menu.logout}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flex: 1,
    backgroundColor: 'white',
  },
  scrollContent: {
    paddingTop: 0,
    paddingHorizontal: 0,
  },
  header: {},
  closeRow: {
    marginBottom: scale({ phone: 6, tablet: 15 }),
    marginRight: scale({ phone: 8, tablet: 20 }),
  },
  closeButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  closeLabel: {
    color: colors['10'],
  },
  headerDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors['6'],
  },
  profileRow: {
    marginTop: scale(24),
    marginBottom: scale(22),
  },
  avatarFrame: {
    borderWidth: 2,
    borderColor: colors.Blue,
    marginLeft: scale({ phone: 12, tablet: 20 }),
    overflow: 'hidden',
  },
  userName: {
    color: colors.Blue,
    maxWidth: scale({ phone: 100, tablet: 200 }),
  },
  userRole: {
    color: colors['10'],
    marginTop: scale(4),
  },
  menuContainer: {},
  menuItem: {
    paddingHorizontal: scale(18),
    paddingTop: scale({ phone: 23, tablet: 20 }),
    paddingBottom: scale(20),
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['6'],
  },
  menuLabel: {
    color: colors.Blue,
  },
}));
